/*---------------React & Libraries-----------------*/
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

/*---------------Material UI Components-----------*/
import { Box, Container, IconButton, Card, CardContent, Typography, useMediaQuery, useTheme, Button, Grid } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AddOutlined from "@mui/icons-material/AddOutlined";

/*---------------Stores---------------------------*/
import { useStore } from "../../app/stores/store";

/*---------------Types & Models-------------------*/
import { CustomerList, CustomerListFilter } from "../../app/models/Customer";
import { CustomerBreadCrumbItem } from "../../app/data/breadCrumbData";

/*---------------Components-----------------------*/
import VirtualizedTable from "../../app/shared/common/Data-grid/DataGrid";
import CustomerSearchForm from "./CustomerSearchForm";
import BreadcrumbsNav from "../../app/shared/common/Miscellaneous/BreadCrumbNav";
import Paginations from "../../app/shared/common/Pagination";

// Constants
const PAGE_LENGTH = 10;

const BADGE_BASE_STYLES = {
  display: "inline-block",
  px: 1.5,
  py: 0.5,
  borderRadius: 10,
  fontSize: "0.75rem",
  fontWeight: 600,
};

const Customers: React.FC = observer(() => {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useState<CustomerListFilter>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { customerStore, commonStore } = useStore();
  const { customers, loadingCustomer, fetchCustomers, customerCount } = customerStore;
  const { setCustomerDetail } = commonStore;
  const navigate = useNavigate();

  useEffect(() => {
    const params: CustomerListFilter = {
      ...searchParams,
      pageNo: page,
      pageLength: PAGE_LENGTH,
    };
    fetchCustomers(params);
  }, [page, searchParams]);

  const handleRowClick = (row: CustomerList) => {
    setCustomerDetail(row);
    navigate(`detail/${row.id}`);
  };

  const handlePage = (newPage: number) => setPage(newPage);

  const handleSearchSubmit = async (values: {
    name: string;
    mobile: string;
    email: string;
    isActive: string | boolean;
  }) => {
    const { mobile, name, email, isActive } = values;
    if (!name && !mobile && !email && typeof isActive === "string") {
      console.warn("All search fields are empty - skipping search");
      return;
    }

    const payload: CustomerListFilter = {
      name,
      mobile,
      email,
    };

    if (typeof isActive === "boolean") {
      payload.isActive = isActive;
    }

    setPage(1); // Reset to page 1 when searching
    setSearchParams(payload);
  };

  const handleReset = () => {
    setSearchParams({});
    setPage(1);
  };

  // Reusable Status Badge Component
  const StatusBadge = ({ isActive }: { isActive: boolean }) => (
    <Box
      sx={{
        ...BADGE_BASE_STYLES,
        bgcolor: isActive ? "success.dark" : "error.dark",
        color: isActive ? "success.contrastText" : "error.contrastText",
      }}
    >
      {isActive ? "Active" : "Inactive"}
    </Box>
  );

  // Mobile Card Component
  const CustomerCard = ({ customer }: { customer: CustomerList }) => {
    const isActive = Boolean(customer.isActive);
    
    return (
      <Card 
        onClick={() => handleRowClick(customer)}
        sx={{ 
          mb: 2, 
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent>
          {/* Top Row: Name and Arrow Icon */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6" fontWeight={600} sx={{ flex: 1, wordBreak: 'break-word', pr: 1 }}>
              {customer.name}
            </Typography>
            <IconButton
              size="small"
              sx={{ color: "primary.main", flexShrink: 0 }}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          </Box>
          
          {/* Middle: Mobile Number and Email */}
          <Box mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center" gap={0.5}>
              <PhoneIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
              <Typography 
                variant="body2" 
                color="primary.main" 
                fontWeight={500}
                sx={{ wordBreak: 'break-word' }}
              >
                {customer.mobile}
              </Typography>
            </Box>
            
            {customer.email && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography 
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {customer.email}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Bottom Row: Customer Type and Status Badges */}
          <Box display="flex" gap={1} flexWrap="wrap">
            <Box
              sx={{
                ...BADGE_BASE_STYLES,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              {customer.customerType.identityCode}
            </Box>
            
            <StatusBadge isActive={isActive} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const customerColumns = [
    {
      key: "name",
      id: "name",
      label: "Outlet Name",
    },
    {
      key: "mobile",
      id: "mobile",
      label: "Outlet Number",
      render: (value: string, row: CustomerList) => {
        return (
          <Box
            onClick={() => handleRowClick(row)}
            sx={{
              cursor: "pointer",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {value}
          </Box>
        );
      },
    },
    {
      key: "email",
      id: "email",
      label: "Email ID",
    },
    {
      key: "customerTypeId",
      id: "customerTypeId",
      label: "Customer Type",
      render: (value: number, row: CustomerList) => {
        return row.customerType.identityCode;
      },
    },
    {
      key: "isActive",
      id: "isActive",
      label: "Status",
      render: (value: boolean | number) => <StatusBadge isActive={Boolean(value)} />,
    },
    {
      key: "actions",
      id: "actions",
      label: "",
      render: (_: any, row: CustomerList) =>
        (
          <IconButton
            size="small"
            onClick={() => handleRowClick(row)}
            sx={{ color: "primary.main" }}
          >
            <KeyboardArrowRightIcon />
          </IconButton>
        )
    },
  ];
  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0 }}>
      <Box>
        <BreadcrumbsNav items={CustomerBreadCrumbItem} />
      </Box>
      <CustomerSearchForm
        onSubmit={handleSearchSubmit}
        handleReset={handleReset}
      />
      <Grid size={12} py={{xs: 0, md: 2}}>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="success"
            startIcon={<AddOutlined />}
            onClick={() => navigate("/customer-onboard/add")}
          >
            Add New Customer
          </Button>
        </Box>
      </Grid>

      {/* Mobile Card View */}
      {isMobile ? (
        <Box sx={{ mt: 2 }}>
          {loadingCustomer ? (
            <Box>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ height: 200 }} />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : customers.length > 0 ? (
            customers.map((customer) => (
              <CustomerCard key={customer.id} customer={customer} />
            ))
          ) : (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 8,
                color: 'text.secondary' 
              }}
            >
              <Typography variant="body1">No customers found</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <VirtualizedTable<CustomerList>
          data={customers}
          columns={customerColumns}
          height={600}
          loading={loadingCustomer}
        />
      )}
      
      {customerCount && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Paginations
            page={page}
            total={customerCount}
            itemsPerPage={PAGE_LENGTH}
            handlePage={handlePage}
          />
        </Box>
      )}
    </Container>
  );
});

export default Customers;
